import React, { useState } from 'react';
import { useBooks } from '../context/BookContext';
import { Trophy, RefreshCw, Star, ListOrdered, ArrowUpNarrowWide } from 'lucide-react';
import RankingFlow from '../components/RankingFlow';

const Bracket = () => {
  const { books, logDuel } = useBooks();
  const [activeTab, setActiveTab] = useState('rankings'); // 'tournament' or 'rankings'
  const [gameStatus, setGameStatus] = useState('idle'); // idle, playing, finished
  const [currentRound, setCurrentRound] = useState([]);
  const [nextRound, setNextRound] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [champion, setChampion] = useState(null);
  const [rankingBook, setRankingBook] = useState(null);

  // Filter only read books
  const readBooks = books.filter(b => b.status === 'read');
  const sortedBooks = [...readBooks].sort((a, b) => (a.rank || 999) - (b.rank || 999));

  const startTournament = () => {
    if (readBooks.length < 2) {
      alert("You need at least 2 read books to start a tournament!");
      return;
    }

    const shuffled = [...readBooks].sort(() => 0.5 - Math.random());
    const count = shuffled.length;
    let size = 2;
    while (size * 2 <= count) size *= 2;
    
    const tournamentBooks = shuffled.slice(0, size);

    setCurrentRound(tournamentBooks);
    setNextRound([]);
    setCurrentMatchIndex(0);
    setGameStatus('playing');
    setChampion(null);
  };

  const handleVote = (winner) => {
    const loser = winner === contender1 ? contender2 : contender1;
    if (loser) logDuel(winner, loser);

    const newNextRound = [...nextRound, winner];
    
    if (currentMatchIndex + 2 >= currentRound.length) {
      if (newNextRound.length === 1) {
        setChampion(newNextRound[0]);
        setGameStatus('finished');
      } else {
        setCurrentRound(newNextRound);
        setNextRound([]);
        setCurrentMatchIndex(0);
      }
    } else {
      setNextRound(newNextRound);
      setCurrentMatchIndex(currentMatchIndex + 2);
    }
  };

  const contender1 = currentRound[currentMatchIndex];
  const contender2 = currentRound[currentMatchIndex + 1];

  if (rankingBook) {
    return <RankingFlow book={rankingBook} onClose={() => setRankingBook(null)} />;
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div style={headerStyle}>
        <Trophy size={32} color="#ffc107" />
        <h2 style={{ margin: 0 }}>The Favorites Garden</h2>
      </div>

      {/* Tabs */}
      <div style={tabContainer}>
        <button 
          style={activeTab === 'rankings' ? activeTabStyle : tabStyle}
          onClick={() => { setActiveTab('rankings'); setGameStatus('idle'); }}
        >
          <ListOrdered size={18} /> Rankings
        </button>
        <button 
          style={activeTab === 'tournament' ? activeTabStyle : tabStyle}
          onClick={() => setActiveTab('tournament')}
        >
          <Trophy size={18} /> Duels
        </button>
      </div>

      {activeTab === 'rankings' && (
        <div style={listStyle}>
          {sortedBooks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999', marginTop: '2rem' }}>
              Finish some books to see your rankings!
            </p>
          ) : (
            sortedBooks.map((book, idx) => (
              <div key={book.id} style={rankItemStyle}>
                <div style={{
                  ...rankNumberStyle,
                  backgroundColor: idx === 0 ? '#ffc107' : (idx === 1 ? '#adb5bd' : (idx === 2 ? '#cd7f32' : '#eee')),
                  color: idx < 3 ? 'white' : '#666'
                }}>
                  {idx + 1}
                </div>
                {book.cover ? (
                  <img src={book.cover} alt="" style={miniCoverStyle} />
                ) : (
                  <div style={miniPlaceholderStyle}>{book.title[0]}</div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{book.title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>{book.author}</div>
                </div>
                <button 
                  onClick={() => setRankingBook(book)}
                  style={rerankBtn}
                  title="Adjust Rank"
                >
                  <ArrowUpNarrowWide size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'tournament' && (
        <div style={{ textAlign: 'center' }}>
          {gameStatus === 'idle' && (
            <div style={cardStyle}>
              <p>Choose between two books at a time to help find your favorites.</p>
              <p style={{ color: '#666' }}>Eligible Books: {readBooks.length}</p>
              <button 
                onClick={startTournament} 
                disabled={readBooks.length < 2}
                style={playButtonStyle}
              >
                Start Matchups
              </button>
            </div>
          )}

          {gameStatus === 'playing' && contender1 && contender2 && (
            <div>
              <h3 style={{ marginBottom: '1rem', color: '#666' }}>
                Round of {currentRound.length}
              </h3>
              <div style={matchupContainer}>
                <div style={contenderCardStyle} onClick={() => handleVote(contender1)}>
                  {contender1.cover ? (
                    <img src={contender1.cover} alt="" style={coverStyle} />
                  ) : (
                    <div style={placeholderStyle}>{contender1.title[0]}</div>
                  )}
                  <h4 style={titleStyle}>{contender1.title}</h4>
                </div>
                <div style={{ fontWeight: 'bold', color: '#999' }}>VS</div>
                <div style={contenderCardStyle} onClick={() => handleVote(contender2)}>
                  {contender2.cover ? (
                    <img src={contender2.cover} alt="" style={coverStyle} />
                  ) : (
                    <div style={placeholderStyle}>{contender2.title[0]}</div>
                  )}
                  <h4 style={titleStyle}>{contender2.title}</h4>
                </div>
              </div>
            </div>
          )}

          {gameStatus === 'finished' && champion && (
            <div style={{ ...cardStyle, backgroundColor: '#fff8e1', border: '2px solid #ffc107' }}>
              <h3>🏆 The Winner 🏆</h3>
              <div style={{ margin: '20px auto', width: '120px' }}>
                {champion.cover ? (
                  <img src={champion.cover} alt="" style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
                ) : (
                   <div style={{ ...placeholderStyle, width: '120px', height: '180px' }}>{champion.title[0]}</div>
                )}
              </div>
              <h2>{champion.title}</h2>
              <button onClick={startTournament} style={secondaryButtonStyle}>
                <RefreshCw size={16} /> Play Again
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Styles
const headerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '1.5rem' };
const tabContainer = { display: 'flex', backgroundColor: '#f0f0f0', borderRadius: '10px', padding: '4px', marginBottom: '1.5rem' };
const tabStyle = { flex: 1, padding: '10px', background: 'none', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#666', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' };
const activeTabStyle = { ...tabStyle, backgroundColor: 'white', color: '#333', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' };

const listStyle = { display: 'flex', flexDirection: 'column', gap: '10px' };
const rankItemStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #eee' };
const rankNumberStyle = { width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' };
const miniCoverStyle = { width: '40px', height: '60px', objectFit: 'cover', borderRadius: '4px' };
const miniPlaceholderStyle = { width: '40px', height: '60px', backgroundColor: '#f0f0f0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontWeight: 'bold' };
const rerankBtn = { background: 'none', border: '1px solid #eee', borderRadius: '8px', padding: '8px', cursor: 'pointer', color: '#999' };

const cardStyle = { backgroundColor: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #eee' };
const playButtonStyle = { backgroundColor: '#333', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '1.1rem', cursor: 'pointer', marginTop: '1rem' };
const matchupContainer = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' };
const contenderCardStyle = { flex: 1, backgroundColor: 'white', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', height: '220px' };
const coverStyle = { width: '80px', height: '120px', objectFit: 'cover', borderRadius: '4px', marginBottom: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' };
const placeholderStyle = { width: '80px', height: '120px', backgroundColor: '#eee', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#aaa', marginBottom: '10px' };
const titleStyle = { margin: 0, fontSize: '0.9rem', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' };
const secondaryButtonStyle = { display: 'flex', alignItems: 'center', gap: '8px', margin: '20px auto 0', padding: '10px 20px', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' };

export default Bracket;
